import React from 'react';
import {
    filter,
    findIndex,
    map,
    sortBy,
    uniq,
    find,
    includes,
    intersectionWith
} from 'lodash';
import { getAssetIcon } from 'helpers/icons';
import { UI_CONSTANTS } from '../constants';
import { allAssetTypes, apiCallStatus } from 'helpers/constants';
import { allAssetTypeFilter } from 'helpers/filterHelper';
import {
    wizardSteps,
    wizardStepStates,
    pageSize,
    MAX_LENGTH_POWERTRAIN_NAME
} from './constants';


export const getWizardDefaultStepStates = () => {
    return {
        [wizardSteps.STEP1]: {
            state: wizardStepStates.ACTIVE,
            acceptAction: null,
            cancelAction: null,
        },
        [wizardSteps.STEP2]: {
            state: wizardStepStates.INACTIVE,
            acceptAction: null,
            cancelAction: null,
        },
        [wizardSteps.STEP3]: {
            state: wizardStepStates.INACTIVE,
            acceptAction: null,
            cancelAction: null,
        },
    };
};

export const setWizardStepState = (wizardSteps, step, newState) => {
    wizardSteps[step].state = newState;
    return wizardSteps;
};

const mapAssets = (assets, addedAssets, assetType = null) => {
    return assets && filter(assets, asset => {
        return assetType && assetType !== allAssetTypes ?
            asset?.assetType === assetType && !find(addedAssets, addedAsset => addedAsset?.id === asset?.id) :
            !find(addedAssets, addedAsset => addedAsset?.id === asset?.id);
    }) || [];
};

// eslint-disable-next-line import/no-unused-modules
export const getAssetTypes = (assets, translate) => {
    const options = [];

    const assetTypes = uniq(map(assets, (asset) => asset?.assetType));

    map(assetTypes, (assetType, indx) => {
        const label = translate(`ABB.Powertrain.Frontend.${assetType?.toLowerCase()}Label`) || assetType;
        options.push({
            id: indx + 1,
            title: <>{getAssetIcon(assetType, UI_CONSTANTS.ICON_SIZES.SMALL)} {label}</>,
            assetType: assetType,
            assetTypeLabel: label,
        });
    });

    return [
        allAssetTypeFilter(translate),
        ...sortBy(options, [option => option?.assetTypeLabel])
    ];
};

const filterAssetsBySearchCriteria = (assets, searchCriteria) => {
    return searchCriteria !== '' ?
        filter(assets, asset => includes(asset?.id, searchCriteria) ||
            includes(asset?.name?.toLowerCase(), searchCriteria?.toLowerCase()) ||
            includes(asset?.serialNumber, searchCriteria)) :
        assets;
};

export const filterAddedAssetsByType = (assets, assetTypeFilter, searchCriteria) => {
    let filteredAssets = [];

    filteredAssets = !assetTypeFilter ?
        assets :
        filter(assets, asset => assetTypeFilter?.assetType === allAssetTypes ?
            true : asset?.assetType === assetTypeFilter?.assetType);

    return filterAssetsBySearchCriteria(filteredAssets, searchCriteria);
};

export const filterAvailableAssetsByType = (assets, addedAssets, assetTypeFilter) => {
    let filteredAssets = [];

    filteredAssets = !assetTypeFilter ?
        mapAssets(assets, addedAssets) :
        mapAssets(assets, addedAssets, assetTypeFilter?.assetType);

    return filteredAssets;
};

// eslint-disable-next-line import/no-unused-modules
export const getPowertrainAssetIds = (powertrain) => {
    return powertrain.assets.map((asset) => asset?.id);
};

// eslint-disable-next-line import/no-unused-modules
export const isPowertrainNameValid = (value) => {
    return value.length <= MAX_LENGTH_POWERTRAIN_NAME;
};

// eslint-disable-next-line import/no-unused-modules
export const powertrainNameShouldChange = (value) => {
    return !(value.length > MAX_LENGTH_POWERTRAIN_NAME + 1 || value === ' ');
};

// eslint-disable-next-line import/no-unused-modules
export const assemblePowertrain = (powertrainId, name, site, assets) => {
    return {
        powertrainId,
        name,
        siteId: site?.siteId,
        assets
    };
};


const filterAssetsByPowertrainSite = (assets, siteId) => {
    return assets?.length && assets.filter((asset) => {
        return asset?.site?.siteId === siteId;
    });
};


/**
 * Enhanced function to disassemble powertrain data from API format to wizard format
 * Handles all wizard steps: basic info, asset selection, and physical connections
 *
 * @param {Object} powertrain - Powertrain data from API
 * @returns {Object} - Wizard-compatible data structure
 */
export const disassemblePowertrain = (powertrain) => {
    if (!powertrain) {
        return {
            powertrainName: '',
            selectedSite: null,
            powertrainID: null,
            addedAssets: [],
            connectedAssets: [],
            notConnectedAssets: [],
            rowVersion: null
        };
    }

    // Combine both connected and not connected assets for the asset selection step
    const connectedAssets = powertrain?.connectedAssets || [];
    const notConnectedAssets = powertrain?.notConnectedAssets || [];

    // Transform assets to wizard format (ensuring consistent structure)
    const allAssets = [
        ...connectedAssets.map(asset => ({
            assetId: asset.assetId,
            id: asset.assetId?.toString(),
            assetName: asset.assetName,
            name: asset.assetName,
            assetType: asset.assetType,
            type: asset.assetType,
            selectedSubType: asset.subType || '',
            subType: asset.subType || '',
            typeConfigId: asset.typeConfigId || '',
            speedRatio: asset.speedRatio || '',
            drivingAssetId: asset.drivingAssetId || 0,
            drivingMotorId: asset.drivingMotorId || 0,
            isConnected: true, // Flag for physical connection step
            site: { siteId: powertrain.siteId }
        })),
        ...notConnectedAssets.map(asset => ({
            assetId: asset.assetId,
            id: asset.assetId?.toString(),
            assetName: asset.assetName,
            name: asset.assetName,
            assetType: asset.assetType,
            type: asset.assetType,
            selectedSubType: asset.subType || '',
            subType: asset.subType || '',
            typeConfigId: asset.typeConfigId || '',
            speedRatio: asset.speedRatio || '',
            drivingAssetId: asset.drivingAssetId || 0,
            drivingMotorId: asset.drivingMotorId || 0,
            isConnected: false, // Flag for physical connection step
            site: { siteId: powertrain.siteId }
        }))
    ];

    // Filter assets by site if needed
    const filteredAssets = powertrain.siteId && allAssets?.length ?
        filterAssetsByPowertrainSite(allAssets, powertrain.siteId) : allAssets;

    return {
        powertrainName: powertrain?.powertrainName || powertrain?.name || '',
        description: powertrain?.description || '',
        selectedSite: {
            siteId: powertrain.siteId,
            siteName: powertrain.siteName,
            id: powertrain.siteId,
            name: powertrain.siteName
        },
        powertrainID: powertrain?.powertrainId,
        powertrainId: powertrain?.powertrainId,
        addedAssets: [...filteredAssets],
        selectedAssets: [...filteredAssets],
        // Preserve physical connection data
        connectedAssets: connectedAssets.map(asset => ({
            ...asset,
            assetId: asset.assetId,
            id: asset.assetId?.toString()
        })),
        notConnectedAssets: notConnectedAssets.map(asset => ({
            ...asset,
            assetId: asset.assetId,
            id: asset.assetId?.toString()
        })),
        // Physical connection configuration
        physicalConnection: {
            configuredConnections: connectedAssets,
            connectionSequence: connectedAssets.map(asset => asset.assetId?.toString()),
            isSkipped: connectedAssets.length === 0 && notConnectedAssets.length > 0
        },
        // Metadata for edit operations
        rowVersion: powertrain?.rowVersion || '',
        isEditMode: true
    };
};

export const addAssetToList = (assets, asset) => {
    const newAssetsList = assets ? assets : [];
    newAssetsList.push(asset);
    return newAssetsList;
};

export const removeAssetFromList = (assets, assetToRemove) => {
    assets.splice(findIndex(assets, asset => asset?.id === assetToRemove?.id), 1);
    return assets;
};

export const getUpdatedAvailableAssets = (availableAssets, action) => {
    const hasMore = action.data?.assets && !(action.data?.assets?.length < pageSize);
    const data = action.loadingStatus !== apiCallStatus.LOADING &&
        action.data?.pageNumber === 1 ? [] : [...availableAssets.data];
    return action.loadingStatus !== apiCallStatus.SUCCESS ? {
        ...availableAssets,
        loadingStatus: action.loadingStatus
    } : {
        ...availableAssets,
        loadingStatus: action.loadingStatus,
        data: data && [...data, ...action.data.assets],
        pageNumber: hasMore ? action.data?.pageNumber + 1 : action.data?.pageNumber,
        hasMore
    };
};

export const formatAddPowertrainRequest = (powertrain) => {
    // Format the request payload according to the API schema
    // Each asset in connectedAssets and notConnectedAssets should have:
    // - powertrainId (0 for new powertrains)
    // - typeConfigId (string)
    // - speedRatio (number)
    // - drivingAssetId (number, references assetId of asset immediately above)
    // - drivingMotorId (number, references assetId of Motor asset above)
    // - subType (string)

    return {
        powertrainName: powertrain?.name || powertrain?.powertrainName,
        siteId: powertrain?.siteId,
        locationId: powertrain?.locationId,
        notConnectedAssets: powertrain?.notConnectedAssets || [],
        connectedAssets: powertrain?.connectedAssets || []
    };
};

// eslint-disable-next-line import/no-unused-modules
export const formatEditPowertrainRequest = (powertrain) => {
    // Format the edit request payload according to the API schema
    // Each asset in connectedAssets and notConnectedAssets should have:
    // - powertrainId (number, the powertrain being edited)
    // - typeConfigId (string)
    // - speedRatio (number)
    // - drivingAssetId (number, references assetId of asset immediately above)
    // - drivingMotorId (number, references assetId of Motor asset above)
    // - subType (string)

    const payload = {
        powertrainId: powertrain?.powertrainId,
        powertrainName: powertrain?.name || powertrain?.powertrainName,
        siteId: powertrain?.siteId,
        locationId: powertrain?.locationId,
        notConnectedAssets: powertrain?.notConnectedAssets || [],
        connectedAssets: powertrain?.connectedAssets || [],
        rowVersion: powertrain?.rowVersion
    };

    return payload;
};

export const formatGetAssetsRequest = (siteId, pageNumber, pageSize) => {
    return {
        siteId,
        pageNumber,
        pageSize,
    };
};
export const formatGetAssetDetailsRequest = (assets) => {
    return {
        assets: assets.map(asset => ({
            assetId: Number(asset.assetId), // Ensure assetId is a number as per API spec
            // Only include assetSubType if it exists
            ...asset.assetSubType && { assetSubType: asset.assetSubType }
        }))
    };
};

export const getAssetListIntersect = (availableAssets, allAssets) => {
    return intersectionWith(
        availableAssets,
        allAssets,
        (available, all) => available.id === all.id);
};
